import PropTypes from 'prop-types';
import React from 'react';
import { css, cx } from 'emotion';
import ReactDOM from 'react-dom';
import ListItem from 'components/core/ListItem';
import Flexbox from 'flexbox-react';
import AbstractLocalStorageComponent from 'components/core/AbstractLocalStorageComponent';
import $ from 'jquery';
import Entity from 'components/core/Entity';
import { ContextMenu, MenuItem, ContextMenuTrigger, connectMenu } from 'react-contextmenu';
import RollupError from 'components/core/RollupError';
import { colorTextGrey } from 'styles/base';
import _ from 'lodash';

const DynamicMenu = (props) => {
    // 'trigger' is not getting passed in, so not working yet to determine if context menu item should be disabled or not
    // see example in DynamicMenu.js of react-contextmenu of what I was trying for...
    const { trigger } = props;
    const menuItems = props.contextMenuItems.map((item) => {
        let disabled = false;
        if (item.disabled && item.disabled === true) {
            disabled = true;
        } else if (props.isContextMenuItemDisabled && props.trigger) {
            disabled = props.isContextMenuItemDisabled(item.label, trigger.name, props.selectedItems);
        }

        return (
            <MenuItem key={item.label} data={item.data} disabled={disabled} onClick={props.onClick}>
                {item.label}
            </MenuItem>
        );
    });
    return (
        <ContextMenu id={props.id}>
            {menuItems}
        </ContextMenu>
    );
};

DynamicMenu.propTypes = {
    id: PropTypes.string.isRequired,
    contextMenuItems: PropTypes.array.isRequired,
    onClick: PropTypes.func.isRequired,
    isContextMenuItemDisabled: PropTypes.func,
    trigger: PropTypes.shape({
        name: PropTypes.string.isRequired
    }),
    selectedItems: PropTypes.object
};

export default class AbstractFilteredList extends AbstractLocalStorageComponent {
    static COLOR_LIGHT_GREY = '#f5f5f5';
    static COLOR_WHITE = 'white';
    static COLOR_RED = 'rgba(190, 89, 89, .3)';

    static propTypes = {
        data: PropTypes.object.isRequired,
        sortedIds: PropTypes.array.isRequired,
        onDoubleClick: PropTypes.func,
        onFilterChanged: PropTypes.func, // called whenever the filter changes
        onSelectionChanged: PropTypes.func, // called whenever the selection changes
        filterable: PropTypes.bool,
        multiSelect: PropTypes.bool,
        onContextMenuItemClicked: PropTypes.func,
        contextMenuItems: PropTypes.array,
        contextMenuId: PropTypes.string,
        isContextMenuItemDisabled: PropTypes.func,
        children: PropTypes.element,
        process: PropTypes.object, //used in subclasses for navigation
        processErrors: PropTypes.object,
        baseFieldPath: PropTypes.array, // the base field path for the list
        asideChildren: PropTypes.bool,
        emptyDataMsg: PropTypes.string,
        onSelectionClick: PropTypes.func,
        subText: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object
        ]),
    };

    static defaultProps = {
        asideChildren: false,
        emptyDataMsg: '',
        multiSelect: true
    };

    constructor(props) {
        super(props);
        this.state = {
            selectedItems: {},
            lastSelectedItem: null
        };

        if (props.useLocalStorage) {
            this.state.filter = '';
        }
    }

    getFilter() {
        return this.getLSState().filter || '';
    }

    setFilter(newFilter) {
        this.setLSState({
            filter: newFilter
        });
    }

    getLastSelectedItem() {
        return this.state.lastSelectedItem;
    }

    getSelectedItems() {
        // Filter out the items that are not selected, so that it won't confuse child components, since
        // the state contains all items, but marks whether it is selected as true or false
        // e.g., { met_rh_mean: true, met_temp_mean: false }
        const selectedItems = {};
        Object.keys(this.state.selectedItems).forEach(itemName => {
            const isSelected = this.state.selectedItems[itemName];
            if (isSelected) {
                selectedItems[itemName] = true;
            } 
        });
        return selectedItems;
    }

    clearSelection = () => {
        this.setState({ selectedItems: {} });
    };

    /**
     * This is called after component is first created.  If componentDidMount() is called, then componentWillReceiveProps()
     * will not be called and vice versa.
     */
    componentDidMount() {
        window.PCM.appContainer.addEventListener('click', this.onDocumentClick);

        // After we mount, make sure our dependent components are refreshed
        this.updateDependentComponents();
    }

    componentWillUnmount() {
        window.PCM.appContainer.removeEventListener('click', this.onDocumentClick);
    }

    /**
     * Called after the component is rendered because of a change.  This method is not called when
     * the component is first created/rendered, instead componentDidMount is called.
     * @param nextProps
     */
    componentDidUpdate = (prevProps, prevState) => {
        // After we get new props, make sure our dependent components are refreshed
        this.updateDependentComponents();
    };

    updateDependentComponents() {
        if (this.props.onSelectionChanged) {
            this.props.onSelectionChanged(this.getSelectedItems(), this.getLastSelectedItem());
        }
    }

    /**
     * When a list item is selected, determine the proper selection based on whether ctrl or shift keys
     * were selected.
     * @param e
     */
    onClick = (e) => {
        const $selectedElement = $(e.target).closest('[data-name]');
        const itemName = $selectedElement.data('name');
        const selectedItems = this.getSelectedItems();

        const ctrlClick = e.ctrlKey || e.metaKey;
        const shiftClick = e.shiftKey;
        const previouslySelected = selectedItems[itemName];
        const container = $(e.target).closest('.filtered-list');
        let newSelectedState = null;
        let lastSelectedItem = this.getLastSelectedItem();
        const selectMyselfOnly = {};
        selectMyselfOnly[itemName] = true;

        if (!this.props.multiSelect) {
            // if only allowing a single selection, select myself only
            newSelectedState = selectMyselfOnly;
            lastSelectedItem = itemName;

        } else if (e.nativeEvent.which === 1) {  // left mouse button pressed

            // If no ctrl/shift key present, we remove all instances of selected and only select the current element
            if (!shiftClick && !ctrlClick) {
                newSelectedState = selectMyselfOnly;
                lastSelectedItem = itemName;

            } else if (ctrlClick) {
                // If ctrl is held, we toggle the selected class from the current element
                newSelectedState = Object.assign({}, selectedItems);
                newSelectedState[itemName] = !selectedItems[itemName];
                if (newSelectedState[itemName]) {
                    lastSelectedItem = itemName;
                }

            } else if (shiftClick) {
                const $selected = container.find('.selected');
                newSelectedState = {};
                if ($selected.length === 0) {
                    // Nothing has previously been selected, so just select current selection
                    newSelectedState = selectMyselfOnly;
                    lastSelectedItem = itemName;

                } else {
                    // If shift is held, we select all rows between this element and the last selected element
                    // in the container
                    let startIndex = null;
                    let endIndex = null;
                    // compute the range that should be selected
                    container.find('.list-item').each((index, element) => {
                        const currentItemName = $(element).data('name');
                        if (currentItemName === this.getLastSelectedItem() || currentItemName === itemName) {
                            if (startIndex === null) {
                                startIndex = index;
                            } else {
                                endIndex = index;
                            }
                        }
                    });

                    // now select that range
                    container.find('.list-item').each((index, element) => {
                        const currentItemName = $(element).data('name');
                        if (index >= startIndex && index <= endIndex) {
                            newSelectedState[currentItemName] = true;
                        }
                    });
                }
            }

        } else if (e.nativeEvent.which === 3 && !previouslySelected) {  // right mouse button pressed
            // They have right clicked off of the current selection, so go ahead and select just this element
            newSelectedState = selectMyselfOnly;
            lastSelectedItem = itemName;
        }

        // If the newState is null, that means nothing changed, so just use the current state
        if (newSelectedState === null) {
            newSelectedState = Object.assign({}, selectedItems);
        }

        if (this.props.onSelectionClick) {
            this.props.onSelectionClick(newSelectedState);
        }

        this.setState({
            selectedItems: newSelectedState,
            lastSelectedItem
        });
    };

    onFilterChanged = () => {
        const inputElem = ReactDOM.findDOMNode(this.refs.filterInput);
        const newFilter = inputElem.value;
        this.setLSState({
            filter: newFilter
        });

        if (this.props.onFilterChanged) {
            this.props.onFilterChanged();
        }
    };

    getFilteredData() {

        let filter = this.getFilter();
        filter = _.escapeRegExp(filter);
        const filtered = [];
        if (!filter) {
            filter = '(.*?)';
        }
        const regex = new RegExp(filter, 'i');

        for (const id of this.props.sortedIds) {
            let value = id;
            if (this.props.data) {
                value = this.props.data[id];
            }
            if (this.match(regex, id, value)) {
                filtered.push(id);
            }
        }

        return filtered;
    }

    onDocumentClick = (e) => {
        const listArea = ReactDOM.findDOMNode(this.refs.box);
        if (!listArea.contains(e.target)) {
            this.setState({
                selectedItems: {},
                lastSelectedItem: null
            });
        }
    };

    getItemClassName() {
        return '';
    }

    getListClassName() {
        return '';
    }

    getListItemsClassName() {
        return 'items';
    }

    getInputClassName() {
        return 'filter-input';
    }

    getPlaceholder() {
        return 'Filter by name or partial name';
    }

    /**
     * Child classes should override to perform a custom filtering function on their list.
     * Default is to filter by the name of each object.
     * @param regex
     * @param name
     * @param value
     * @returns {boolean}
     * @private
     */
    match(regex, name, value) {
        return (name.search(regex) > -1);
    }

    renderContextMenu() {
        const ConnectedMenu = connectMenu(this.props.contextMenuId)(DynamicMenu);
        if (this.props.contextMenuItems) {
            return (
                <ConnectedMenu
                    contextMenuItems={this.props.contextMenuItems}
                    onClick={this.props.onContextMenuItemClicked}
                    selectedItems={this.getSelectedItems()}
                    isContextMenuItemDisabled={this.props.isContextMenuItemDisabled}
                />
            );
        } else {
            return '';
        }
    }

    getPropsFromContextMenuItem = (props) => {
        //return the props defined in ContextMenuTrigger that are needed when 'doing' whatever is selected
        //passing in the selected items for now while Carina refactors
        return {
            name: props.name,
            contextMenuId: this.props.contextMenuId,
            selectedItems: this.getSelectedItems()
        };
    };

    renderContextMenuWrappedItem = (name, jsxItem) => {
        return (
            <ContextMenuTrigger
                id={this.props.contextMenuId} name={name}
                holdToDisplay={500}
                collect={this.getPropsFromContextMenuItem}
                key={name}
            >
                {jsxItem}
            </ContextMenuTrigger>
        );
    };

    renderEntity = (name, children, bold) => {
        //each item in the list will no longer render its own popup toolbar with nav & add new buttons (using context menu instead)
        return (
            <Entity
                key={"1"}
                value={name}
                bold={bold}
                children={children}
                validate={this.props.validate}
            />
        );
    };

    renderItem(name, value, selected) {
        return (
            <ListItem key={name}
                name={name}
                selected={selected}
                className={this.getItemClassName()}
                onClick={this.onClick}
                onDoubleClick={this.props.onDoubleClick}
            >
                {this.renderEntity(name)}
            </ListItem>
        );
    }

    renderCheckboxFilters() {
        return (
            <div />
        );
    }

    /**
     * Subclasses can override this method to draw a custom header on their list (making it look more like a table)
     * @returns {Component | null}
     */
    renderHeaderSection() {
        return null;
    }

    /**
     * Look for root level error on the list field
     * (TODO: we are only recording one error at a time, so only one will be visible)
     */
    renderListErrors() {
        let errorsComponent = null;
        if (this.props.processErrors && this.props.baseFieldPath) {
            errorsComponent = (
                <div className="errors">
                    <RollupError
                        fieldPath={this.props.baseFieldPath}
                        processErrors={this.props.processErrors}
                        process={this.props.process}
                        includeChildren
                    />
                </div>
            );
        }
        return errorsComponent;
    }

    /**
     * Render a section containing filter components and the results of the latest filter.
     * Subclasses can override to change the look and feel of the filter or to add additional
     * components to filter on (like check boxes).
     * @param filteredLength - the length of the list after the filter was applied
     * @param length - the total length of the list before filtering
     * @returns Component
     */
    renderFilterSection(filteredLength, length) {
        const filter = this.getFilter();
        const wrapperStyle = css`flex-shrink: 0;`;

        // Only show the filter box if we actually have data in the table
        if (this.props.filterable) {
            return (
                <Flexbox flexDirection="column" className={cx('filter-wrapper', wrapperStyle)}>
                    <input
                        flex={1}
                        ref="filterInput"
                        className={this.getInputClassName()}
                        type="text" placeholder={this.getPlaceholder()}
                        onChange={this.onFilterChanged}
                        value={filter}
                    />
                    {this.renderCheckboxFilters()}
                    <p className="found">Showing {filteredLength} of {length}: </p>
                </Flexbox>
            );
        } else {
            return null;
        }
    }

    renderItems(filteredIds, selectedItems) {
        return filteredIds.map((id) => {
            let value = id;
            if (this.props.data) {
                value = this.props.data[id];
            }

            let selected = false;
            if (selectedItems[id]) {
                selected = true;
            }

            const item = this.renderItem(id, value, selected);

            if (item !== null && this.props.contextMenuId) {
                return this.renderContextMenuWrappedItem(id, item);
            } else {
                return item;
            }
        });
    }

    renderList() {
        const className = `filtered-list ${this.getListClassName()}`;
        const filteredIds = this.getFilteredData();
        const selectedItems = this.getSelectedItems();
        const length = this.props.sortedIds.length;
        const filteredLength = filteredIds.length;
        const items = this.renderItems(filteredIds, selectedItems);
        const emptyStyles = css`
            color: ${colorTextGrey};
            text-align: center;
            font-style: italic;
            padding: 5px 0;
            width: 100%;
            display: inline-block;
        `;

        return (
            <Flexbox flexDirection="column" style={{ flexGrow: 1 }} className={className} ref="list">
                {this.renderFilterSection(filteredLength, length)}
                {this.renderHeaderSection()}
                <div className={this.getListItemsClassName()}>
                    {items.length > 0 ?
                        items
                        :
                        <span className={cx(emptyStyles)}>{this.props.emptyDataMsg}</span>
                    }
                </div>
                {this.renderContextMenu()}
            </Flexbox>
        );
    }

    render() {
        let children = null;
        if (this.props.children) {
            children = React.cloneElement(this.props.children, {
                // pass selected items down to my children (i.e., header and toolbar)
                selectedItems: this.getSelectedItems(),
                lastSelectedItem: this.getLastSelectedItem()
            });
        }

        const subText = this.props.subText ? this.props.subText : null;

        return (
            <Flexbox className={this.props.className}>
                {!this.props.asideChildren ?
                    <Flexbox className="filtered-list-container" flexDirection="column" ref="box" flexGrow={1}>
                        {children}
                        {subText}
                        {this.renderList()}
                        {/*{this.renderListErrors()}*/}
                    </Flexbox>
                    :
                    <Flexbox className="filtered-list-container" flexDirection="row" ref="box">
                        {this.renderList()}
                        {/*{this.renderListErrors()}*/}
                        {children}
                    </Flexbox>
                }
            </Flexbox>
        );

    }

}



// WEBPACK FOOTER //
// ./src/components/core/AbstractFilteredList.js